import { useEffect, useRef } from "react";

/**
 * Attach the returned ref to a zero-height sentinel `<div>` at the bottom
 * of a list — when that sentinel scrolls into view (with 200px of headroom
 * so we kick off the fetch *before* the user visibly hits the bottom),
 * `onVisible` fires.
 *
 * Pass `enabled = hasNextPage && !isFetchingNextPage` so we stop observing
 * when there's nothing left to load and don't double-fire while a page is
 * in flight. `fetchNextPage` from TanStack's `useInfiniteQuery` is stable
 * across renders, so passing it directly as `onVisible` won't thrash the
 * observer.
 */
export function useInfiniteScrollSentinel<T extends HTMLElement = HTMLDivElement>(
  onVisible: () => void,
  enabled: boolean,
) {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onVisible();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, onVisible]);

  return ref;
}
